import { Entry } from 'contentful';
import { PageProps } from 'libs/contentful/queries/page/props';
import { RelatedArticleFlexibleProps, RelatedArticleProps, ResourceProps } from 'libs/contentful/props';
import { buildIncludesMap, isTypenameObject } from './type-guards';
import { annotateModule, flattenModules, removeUnresolvedCollectionItems, wrapCollection } from './page-structure';
import {
  hydrateResourceFieldsFromIncludes,
  normalizeAccordionItemAccordionCopyInlineShortcodesByPageSlug,
  normalizeArticleGridFlexibleBlogItems,
  normalizeBlogDateStrings,
  normalizeCalloutIconCardLinks,
  normalizeCollections,
  normalizeContentBlockEmbeddedDisclosures,
  normalizeCtaLinks,
  normalizeDisclosureGroupingsDisclosures,
  normalizeDisclosuresInToggleItemAccordionCopy,
  normalizeInternalPageLinks,
  normalizeMediaCopyByPageSlug,
  normalizeMediaCopySliderEntryCollection,
  normalizeNavMainLinks,
  normalizeNavTopBarLink,
  normalizeOptionalFields,
  normalizeShortcodeFragmentsByPageSlug,
  normalizeUndefinedToNull,
  removeEditorialFields,
  removeExperimentFields,
  removeNestedContentfulMetadata,
  removeSysFromAssetFields,
  removeSysFromContentTypes,
  removeTypenameFromDataContainers,
  stripCardHeadlineRteFromBentoBoxLargeCard,
  stripCardTypenameInCardCarousel,
  stripEmptyAccordionHyperlinkKeys,
  stripSysFromHomepageHeroImages,
  normalizeCardsIconItemCardIcon,
} from './normalizers';
import { adaptEntry, resetAdaptedCache } from './transforms';

/**
 * Adapts a REST Entry to PageProps format.
 * This is the main entry point for the adapter.
 *
 * @param entry - The REST SDK Entry response
 * @param includes - The includes array from the REST SDK response
 * @returns PageProps compatible with downstream transformers
 */
export function adaptRESTtoPageProps(entry: Entry, includes: { Entry?: unknown[]; Asset?: unknown[] } = {}): PageProps {
  // Clear the adapted cache for each new page adaptation
  resetAdaptedCache();

  // Build includes map for link resolution
  const includesMap = buildIncludesMap(includes);
  // Ensure the root page entry can be resolved via Link objects (it is not guaranteed to appear in includes.Entry)
  if (entry?.sys?.id && typeof entry.sys.id === 'string') {
    includesMap.entries.set(entry.sys.id, entry);
  }

  // First, adapt the entry structure
  // Pass wrapCollections: false for page entry - page-level collections are handled below
  const processed = new WeakSet<object>();
  const adapted = adaptEntry(entry, includesMap, processed, false) as Record<string, unknown>;

  // Convert array fields to collections
  if (adapted.modules && Array.isArray(adapted.modules)) {
    // Keep modules as array for now, will be flattened below
  }

  // Wrap collections - always create even if empty to match GraphQL
  // Note: headerBanner items are modules and get hasExperiments
  if (adapted.headerBanner && Array.isArray(adapted.headerBanner)) {
    adapted.headerBannerCollection = wrapCollection(
      adapted.headerBanner.map(item => annotateModule(item as Record<string, unknown>)),
    );
    delete adapted.headerBanner; // Remove original array
  } else {
    adapted.headerBannerCollection = { items: [] };
  }

  // Note: disclosuresCollection items ARE treated as modules and get hasExperiments
  if (adapted.disclosures && Array.isArray(adapted.disclosures)) {
    adapted.disclosuresCollection = wrapCollection(
      adapted.disclosures.map(item => annotateModule(item as Record<string, unknown>)),
    );
    delete adapted.disclosures; // Remove original array
  } else {
    adapted.disclosuresCollection = { items: [] };
  }

  // disclosuresBottomCollection - items ARE treated as modules and get hasExperiments (same as disclosuresCollection)
  if (adapted.disclosuresBottom && Array.isArray(adapted.disclosuresBottom)) {
    adapted.disclosuresBottomCollection = wrapCollection(
      adapted.disclosuresBottom.map(item => annotateModule(item as Record<string, unknown>)),
    );
    delete adapted.disclosuresBottom; // Remove original array
  } else {
    adapted.disclosuresBottomCollection = { items: [] };
  }

  // bottomModulesCollection (for PageBlogPostFlexible pages only) - modules need annotation
  // Always create this collection for the relevant page type, even if empty - GraphQL returns { items: [] } when undefined
  if (adapted.__typename === 'PageBlogPostFlexible') {
    if (adapted.bottomModules && Array.isArray(adapted.bottomModules)) {
      adapted.bottomModulesCollection = wrapCollection(
        adapted.bottomModules
          .filter(item => isTypenameObject(item as Record<string, unknown>))
          .map(item => annotateModule(item as Record<string, unknown>)),
      );
      delete adapted.bottomModules; // Remove original array
    } else {
      adapted.bottomModulesCollection = { items: [] };
    }
  }

  // customQueryStringParametersCollection (no annotation needed - simple data objects)
  // Always create this collection, even if empty - GraphQL returns { items: [] } when undefined
  if (adapted.customQueryStringParameters && Array.isArray(adapted.customQueryStringParameters)) {
    adapted.customQueryStringParametersCollection = wrapCollection(
      adapted.customQueryStringParameters as Array<Record<string, unknown>>,
    );
    delete adapted.customQueryStringParameters; // Remove original array
  } else {
    adapted.customQueryStringParametersCollection = { items: [] };
  }

  // Annotate top-level sections
  if (adapted.nav) {
    if (isTypenameObject(adapted.nav)) {
      adapted.nav = annotateModule(adapted.nav);
    } else {
      adapted.nav = null;
    }
  }
  if (adapted.hero) {
    if (isTypenameObject(adapted.hero)) {
      adapted.hero = annotateModule(adapted.hero);
    } else {
      adapted.hero = null;
    }
    // GraphQL output includes statsigExperimentId for some top-level hero types (defaults to empty string)
    if (typeof adapted.hero === 'object' && adapted.hero !== null) {
      const hero = adapted.hero as Record<string, unknown>;
      if (hero.__typename === 'HeroConsolidated' || hero.__typename === 'SectionHero') {
        const statsigExperiment = hero.statsigExperiment;
        if (
          statsigExperiment &&
          typeof statsigExperiment === 'object' &&
          !Array.isArray(statsigExperiment) &&
          'sys' in statsigExperiment
        ) {
          const sys = (statsigExperiment as Record<string, unknown>).sys;
          if (sys && typeof sys === 'object' && !Array.isArray(sys)) {
            const id = (sys as Record<string, unknown>).id;
            if (typeof id === 'string') {
              hero.statsigExperimentId = id;
            }
          }
        }
        if (!('statsigExperimentId' in hero)) {
          hero.statsigExperimentId = '';
        }
      }
    }
  }
  if (adapted.footer) {
    if (isTypenameObject(adapted.footer)) {
      adapted.footer = annotateModule(adapted.footer);
    } else {
      adapted.footer = null;
    }
  }
  if (adapted.pageVisualSettings) {
    if (isTypenameObject(adapted.pageVisualSettings)) {
      adapted.pageVisualSettings = annotateModule(adapted.pageVisualSettings);
    } else {
      adapted.pageVisualSettings = null;
    }
  }

  // Flatten modules
  const flattenedModules = flattenModules(adapted).filter(isTypenameObject);
  const annotatedModules = flattenedModules.map(m => {
    const annotated = annotateModule(m);
    if (!('statsigExperimentId' in annotated)) {
      return annotated;
    }
    const { statsigExperimentId: _statsigExperimentId, ...rest } = annotated;
    void _statsigExperimentId;
    return rest;
  });
  adapted.modulesCollection = wrapCollection(annotatedModules);

  // Remove original module array fields that have been flattened
  delete adapted.modules;

  // Set page-level hasExperiments if any module or top-level section has experiments
  const moduleHasExperiments = annotatedModules.some(m => m.hasExperiments === true);
  const navHasExperiments = adapted.nav ? (adapted.nav as Record<string, unknown>).hasExperiments === true : false;
  const heroHasExperiments = adapted.hero ? (adapted.hero as Record<string, unknown>).hasExperiments === true : false;
  const footerHasExperiments = adapted.footer
    ? (adapted.footer as Record<string, unknown>).hasExperiments === true
    : false;
  const pageVisualSettingsHasExperiments = adapted.pageVisualSettings
    ? (adapted.pageVisualSettings as Record<string, unknown>).hasExperiments === true
    : false;
  const disclosuresHasExperiments =
    adapted.disclosuresCollection &&
    (adapted.disclosuresCollection as { items: Array<Record<string, unknown>> }).items?.some(
      m => m.hasExperiments === true,
    );
  const headerBannerHasExperiments =
    adapted.headerBannerCollection &&
    (adapted.headerBannerCollection as { items: Array<Record<string, unknown>> }).items?.some(
      m => m.hasExperiments === true,
    );

  adapted.hasExperiments =
    moduleHasExperiments ||
    navHasExperiments ||
    heroHasExperiments ||
    footerHasExperiments ||
    pageVisualSettingsHasExperiments ||
    disclosuresHasExperiments ||
    headerBannerHasExperiments;

  // Ensure optional fields that GraphQL returns as null are explicitly set
  if (adapted.__typename === 'Page' && !('pageColor' in adapted)) {
    adapted.pageColor = null;
  }
  if (!('footerColor' in adapted)) {
    adapted.footerColor = null;
  }
  if (!('showFooterKeyline' in adapted)) {
    adapted.showFooterKeyline = null;
  }
  if (adapted.__typename === 'Page' && !('pageVisualSettings' in adapted)) {
    adapted.pageVisualSettings = null;
  }
  if (!('seoAllowSearchEnginesToIndex' in adapted)) {
    adapted.seoAllowSearchEnginesToIndex = null;
  }
  if (!('seoAllowSearchEnginesToFollow' in adapted)) {
    adapted.seoAllowSearchEnginesToFollow = null;
  }
  if (!('hero' in adapted)) {
    adapted.hero = null;
  }
  if (!('nav' in adapted)) {
    adapted.nav = null;
  }
  if (!('footer' in adapted)) {
    adapted.footer = null;
  }
  if (adapted.__typename === 'Page' && !('schema' in adapted)) {
    adapted.schema = null;
  }
  if (!('seoCanonical' in adapted)) {
    adapted.seoCanonical = null;
  }
  if (!('seoDescription' in adapted)) {
    adapted.seoDescription = null;
  }
  if (!('seoTitle' in adapted)) {
    adapted.seoTitle = null;
  }
  if (!('seoOpenGraphTitle' in adapted)) {
    adapted.seoOpenGraphTitle = null;
  }
  if (!('seoOpenGraphDescription' in adapted)) {
    adapted.seoOpenGraphDescription = null;
  }
  if (!('seoOpenGraphImage' in adapted)) {
    adapted.seoOpenGraphImage = null;
  }

  // Remove editorial fields that GraphQL doesn't query
  removeEditorialFields(adapted);

  // Remove raw experiment payload fields (we only keep derived flags like hasExperiments)
  removeExperimentFields(adapted);

  // Drop unresolved collection items (sys.id present but missing __typename), matching GraphQL fixture behavior.
  removeUnresolvedCollectionItems(adapted);

  // Normalize optional fields to null for all content types (BEFORE removing typenames!)
  normalizeOptionalFields(adapted);

  // Normalize disclosure shapes for DisclosureGroupings.disclosures items
  normalizeDisclosureGroupingsDisclosures(adapted);

  // Normalize disclosure shapes for embedded disclosures inside ContentBlock.copy rich text
  normalizeContentBlockEmbeddedDisclosures(adapted);

  // Some recorded fixtures contain Resource fields that may be omitted during adaptation; hydrate from includes.
  hydrateResourceFieldsFromIncludes(adapted, includesMap, processed);

  // Some asset fields are queried without sys in GraphQL fixtures
  removeSysFromAssetFields(adapted);

  // Certain content types include __typename but omit sys in GraphQL fixtures
  removeSysFromContentTypes(adapted);

  // Normalize minimal asset shapes for CardsIconItem.cardIcon
  normalizeCardsIconItemCardIcon(adapted);

  // Normalize contextual Link shapes for navigation items
  normalizeNavMainLinks(adapted);

  // Normalize Nav.topBarLink to match GraphQL fragments (no __typename on the object)
  normalizeNavTopBarLink(adapted);

  // Normalize contextual Link shapes (e.g. `cta` fields queried without sys/__typename)
  normalizeCtaLinks(adapted);

  // Normalize internalPage link targets to match GraphQL fragment shapes
  normalizeInternalPageLinks(adapted);

  // Normalize CalloutIconCard.link to match GraphQL fragments (no __typename on Link)
  normalizeCalloutIconCardLinks(adapted);

  // Normalize all collections to match GraphQL output shape
  normalizeCollections(adapted);

  // Normalize PageBlogPostFlexible entries within ArticleGrid.articlesCollection
  // Pass includesMap so we can look up raw hero data for circular reference cases
  normalizeArticleGridFlexibleBlogItems(adapted, includesMap);

  // PageSection uses modulesCollection in GraphQL
  if (adapted.modulesCollection && typeof adapted.modulesCollection === 'object') {
    for (const item of (adapted.modulesCollection as { items?: unknown[] }).items ?? []) {
      if (!item || typeof item !== 'object' || Array.isArray(item)) continue;
      const obj = item as Record<string, unknown>;
      if (obj.__typename === 'PageSection') {
        const modules = obj.modules;
        if (modules && typeof modules === 'object' && !Array.isArray(modules) && 'items' in modules) {
          obj.modulesCollection = modules;
          delete obj.modules;
        }
      }
    }
  }

  // CardCarousel card items are treated as data objects in recorded fixtures (no __typename)
  stripCardTypenameInCardCarousel(adapted);

  // Normalize MediaCopy fragment differences based on page slug
  normalizeMediaCopyByPageSlug(adapted);
  normalizeMediaCopySliderEntryCollection(adapted);
  stripCardHeadlineRteFromBentoBoxLargeCard(adapted);
  normalizeShortcodeFragmentsByPageSlug(adapted);
  normalizeAccordionItemAccordionCopyInlineShortcodesByPageSlug(adapted);
  normalizeDisclosuresInToggleItemAccordionCopy(adapted);
  normalizeBlogDateStrings(adapted);
  stripSysFromHomepageHeroImages(adapted);

  // Remove __typename from simple data container types
  removeTypenameFromDataContainers(adapted);

  // Remove contentfulMetadata from nested entries (GraphQL doesn't return it)
  removeNestedContentfulMetadata(adapted, true);

  // Accordion modules omit empty hyperlink keys on AccordionItem.accordionCopy in recorded fixtures
  stripEmptyAccordionHyperlinkKeys(adapted);

  // Normalize undefined to null to match GraphQL behavior
  const normalized = normalizeUndefinedToNull(adapted) as Record<string, unknown>;

  return normalized as unknown as PageProps;
}

/**
 * Result type for adaptEntriesForArticleGrid - can contain any of the supported article types.
 */
export type ArticleGridItem = RelatedArticleFlexibleProps | RelatedArticleProps | ResourceProps;

/**
 * Resolved asset shape that matches MediaAsset requirements.
 */
interface ResolvedAsset {
  url: string;
  title: string;
  contentType: string;
  description?: string;
  width?: number;
  height?: number;
}

/**
 * Helper to resolve an asset link to a MediaAsset-like object.
 */
function resolveAsset(
  assetLink: { sys?: { id?: string } } | undefined,
  includesMap: ReturnType<typeof buildIncludesMap>,
): ResolvedAsset | undefined {
  if (!assetLink?.sys?.id) return undefined;

  const asset = includesMap.assets.get(assetLink.sys.id);
  if (!asset) return undefined;

  const assetFields = (asset as Record<string, unknown>).fields as Record<string, unknown> | undefined;
  const assetFile = assetFields?.file as Record<string, unknown> | undefined;
  const url = assetFile?.url;
  const title = assetFields?.title;
  const contentType = assetFile?.contentType;
  const imageDetails = (assetFile?.details as Record<string, unknown> | undefined)?.image as
    | Record<string, unknown>
    | undefined;

  if (typeof url !== 'string' || typeof title !== 'string' || typeof contentType !== 'string') {
    return undefined;
  }

  return {
    url,
    title,
    contentType,
    description: typeof assetFields?.description === 'string' ? assetFields.description : undefined,
    width: typeof imageDetails?.width === 'number' ? imageDetails.width : undefined,
    height: typeof imageDetails?.height === 'number' ? imageDetails.height : undefined,
  };
}

/**
 * Helper to resolve a category link.
 */
function resolveCategory(
  categoryLink: { sys?: { id?: string; linkType?: string } } | undefined,
  includesMap: ReturnType<typeof buildIncludesMap>,
): { label: string; url?: string } | undefined {
  if (!categoryLink?.sys?.id || categoryLink.sys.linkType !== 'Entry') return undefined;

  const categoryEntry = includesMap.entries.get(categoryLink.sys.id) as Record<string, unknown> | undefined;
  if (!categoryEntry?.fields) return undefined;

  const categoryFields = categoryEntry.fields as Record<string, unknown>;
  return {
    label: categoryFields.label as string,
    url: categoryFields.url as string | undefined,
  };
}

/**
 * Adapts a PageBlogPostFlexible entry to RelatedArticleFlexibleProps.
 */
function adaptFlexibleBlogPost(
  entry: Entry,
  includesMap: ReturnType<typeof buildIncludesMap>,
): RelatedArticleFlexibleProps | null {
  const fields = entry.fields as Record<string, unknown>;
  const sys = entry.sys;

  if (!fields || !sys?.id) return null;

  const slug = fields.slug as string | undefined;
  if (!slug) return null;

  // Resolve hero data - articleTitle requires headline
  const heroLink = fields.hero as { sys?: { id?: string; linkType?: string } } | undefined;
  let articleTitle: RelatedArticleFlexibleProps['articleTitle'] | undefined;
  let articleImage: RelatedArticleFlexibleProps['articleImage'] | undefined;

  if (heroLink?.sys?.id && heroLink.sys.linkType === 'Entry') {
    const heroEntry = includesMap.entries.get(heroLink.sys.id) as Record<string, unknown> | undefined;
    if (heroEntry) {
      const heroFields = heroEntry.fields as Record<string, unknown> | undefined;
      const heroSys = heroEntry.sys as Record<string, unknown> | undefined;
      const heroContentType = heroSys?.contentType as Record<string, unknown> | undefined;
      const heroContentTypeSys = heroContentType?.sys as Record<string, unknown> | undefined;
      const heroContentTypeId = heroContentTypeSys?.id as string | undefined;

      if (heroContentTypeId === 'heroConsolidated' && heroFields && heroFields.headline) {
        articleTitle = {
          headline: heroFields.headline as RelatedArticleFlexibleProps['articleTitle']['headline'],
        };

        const desktopMediaLink = heroFields.desktopMedia as { sys?: { id?: string } } | undefined;
        const resolvedMedia = resolveAsset(desktopMediaLink, includesMap);
        if (resolvedMedia) {
          articleImage = { desktopMedia: resolvedMedia };
        }
      }
    }
  }

  // Skip entries without a valid hero/headline
  if (!articleTitle) return null;

  const category = resolveCategory(fields.category as { sys?: { id?: string; linkType?: string } }, includesMap);
  const metadata = entry.metadata as { concepts?: Array<{ sys: { id: string } }> } | undefined;
  const categories = metadata?.concepts ? { items: metadata.concepts.map(c => ({ id: c.sys.id })) } : { items: [] };

  return {
    __typename: 'PageBlogPostFlexible',
    articleTitle,
    articleImage,
    slug,
    category,
    categories,
    sys: { id: sys.id },
  };
}

/**
 * Adapts a BlogPostPage (blogPage) entry to RelatedArticleProps.
 */
function adaptBlogPostPage(entry: Entry, includesMap: ReturnType<typeof buildIncludesMap>): RelatedArticleProps | null {
  const fields = entry.fields as Record<string, unknown>;
  const sys = entry.sys;

  if (!fields || !sys?.id) return null;

  const slug = fields.slug as string | undefined;
  if (!slug) return null;

  // BlogPostPage has articleTitle directly as a rich text field
  const articleTitle = fields.articleTitle;
  if (!articleTitle) return null;

  // Resolve articleImage asset
  const articleImageLink = fields.articleImage as { sys?: { id?: string } } | undefined;
  const articleImage = resolveAsset(articleImageLink, includesMap);

  const category = resolveCategory(fields.category as { sys?: { id?: string; linkType?: string } }, includesMap);
  const metadata = entry.metadata as { concepts?: Array<{ sys: { id: string } }> } | undefined;
  const categories = metadata?.concepts ? { items: metadata.concepts.map(c => ({ id: c.sys.id })) } : { items: [] };

  return {
    __typename: 'RelatedArticle',
    articleTitle: articleTitle as RelatedArticleProps['articleTitle'],
    articleImage: articleImage as RelatedArticleProps['articleImage'],
    slug,
    category,
    categories,
    sys: { id: sys.id },
  };
}

/**
 * Adapts a Resource entry to ResourceProps.
 */
function adaptResource(entry: Entry, includesMap: ReturnType<typeof buildIncludesMap>): ResourceProps | null {
  const fields = entry.fields as Record<string, unknown>;
  const sys = entry.sys;

  if (!fields || !sys?.id) return null;

  // Resource requires resourceCopy
  const resourceCopy = fields.resourceCopy;
  if (!resourceCopy) return null;

  // Resolve resourceIcon asset
  const resourceIconLink = fields.resourceIcon as { sys?: { id?: string } } | undefined;
  const resourceIcon = resolveAsset(resourceIconLink, includesMap);

  // Resolve resourcePage link
  let resourcePage: ResourceProps['resourcePage'];
  const resourcePageLink = fields.resourcePage as { sys?: { id?: string; linkType?: string } } | undefined;
  if (resourcePageLink?.sys?.id && resourcePageLink.sys.linkType === 'Entry') {
    const pageEntry = includesMap.entries.get(resourcePageLink.sys.id) as Record<string, unknown> | undefined;
    if (pageEntry?.fields) {
      const pageFields = pageEntry.fields as Record<string, unknown>;
      const pageSys = pageEntry.sys as { id?: string } | undefined;
      if (pageFields.slug && pageSys?.id) {
        resourcePage = {
          slug: pageFields.slug as string,
          sys: { id: pageSys.id },
        };
      }
    }
  }

  return {
    __typename: 'Resource',
    resourceCopy: resourceCopy as ResourceProps['resourceCopy'],
    resourceIcon: resourceIcon as ResourceProps['resourceIcon'],
    resourceLabelRte: fields.resourceLabelRte as ResourceProps['resourceLabelRte'],
    resourcePage,
    url: fields.url as string | undefined,
    sys: { id: sys.id },
  };
}

/**
 * Adapts an array of REST entries to the shapes expected by ArticleGrid.
 * Supports multiple content types: PageBlogPostFlexible, BlogPostPage (blogPage), and Resource.
 *
 * @param entries - Array of REST SDK Entry responses
 * @param includes - The includes object from the REST SDK response
 * @returns Array of article items compatible with ArticleGrid
 */
export function adaptEntriesForArticleGrid(
  entries: Entry[],
  includes: { Entry?: unknown[]; Asset?: unknown[] } = {},
): ArticleGridItem[] {
  const includesMap = buildIncludesMap(includes);

  // Add all entries to the includes map so they can be resolved
  for (const entry of entries) {
    if (entry?.sys?.id && typeof entry.sys.id === 'string') {
      includesMap.entries.set(entry.sys.id, entry);
    }
  }

  const results: ArticleGridItem[] = [];

  for (const entry of entries) {
    const sys = entry.sys as { contentType?: { sys?: { id?: string } } } | undefined;
    const contentTypeId = sys?.contentType?.sys?.id;

    let adapted: ArticleGridItem | null = null;

    switch (contentTypeId) {
      case 'pageBlogPostFlexible':
        adapted = adaptFlexibleBlogPost(entry, includesMap);
        break;
      case 'blogPage':
        adapted = adaptBlogPostPage(entry, includesMap);
        break;
      case 'resource':
        adapted = adaptResource(entry, includesMap);
        break;
    }

    if (adapted) {
      results.push(adapted);
    }
  }

  return results;
}
